import { Box, Typography } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router';
import { Tabs } from 'src/common';
import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import { SecurityDashboardToolbar } from '../../shared/securityDashboardToolbar';
import EndpointActivityTimelineTab from './EndpointActivityTimelineTab';
import EndpointComplianceTab from './EndpointComplianceTab';
import EndpointDashboardTab from './EndpointDashboardTab';
import EndpointEventsTab from './EndpointEventsTab';
import EndpointTechnicalDetailsTab from './EndpointTechnicalDetailsTab';
import EndpointVulnerabilitiesTab from './EndpointVulnerabilitiesTab';

type EndpointDetailsLocationState = {
  endpointName?: string;
};

type EndpointDetailsTab =
  | 'dashboard'
  | 'events'
  | 'vulnerabilities'
  | 'activity-timeline'
  | 'compliance'
  | 'technical-details';

const tabs: Array<{ label: string; value: EndpointDetailsTab }> = [
  { label: 'Dashboard', value: 'dashboard' },
  { label: 'Events', value: 'events' },
  { label: 'Vulnerabilities', value: 'vulnerabilities' },
  { label: 'Activity Timeline', value: 'activity-timeline' },
  { label: 'Compliance', value: 'compliance' },
  { label: 'Technical Details', value: 'technical-details' },
];

const EndpointDetails: FC = () => {
  const { systemWazuhAgentId: encodedSystemWazuhAgentId = '' } =
    useParams<{ systemWazuhAgentId: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<EndpointDetailsTab>('dashboard');
  const [searchQuery, setSearchQuery] = useState('');

  const systemWazuhAgentId = decodeURIComponent(encodedSystemWazuhAgentId).trim();
  const endpointName = (location.state as EndpointDetailsLocationState | null)?.endpointName?.trim();
  const displayName = endpointName || systemWazuhAgentId;

  const breadcrumbData = {
    links: [
      { name: 'Endpoints', to: SECURITY_ENDPOINTS_HREF },
      { name: displayName, to: '' },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle(`Security — ${displayName}`)}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{
          minHeight: '100%',
          boxSizing: 'border-box',
          gap: 16,
          minWidth: 0,
        }}
      >
        <SimpleBreadCrumb data={breadcrumbData} />

        <Box display="flex" flexDirection="column" style={{ gap: 8, minWidth: 0 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ gap: 12 }}
          >
            <Typography
              component="h1"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 22,
                fontWeight: 700,
                color: '#1E293B',
                flexShrink: 0,
              }}
            >
              {displayName}
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{
                gap: 12,
                flex: '1 1 280px',
                justifyContent: 'flex-end',
                minWidth: 0,
              }}
            >
              <Box style={{ flex: '1 1 280px', maxWidth: 560, minWidth: 200 }}>
                <SecurityDashboardToolbar
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                  layout="full"
                  showClientFilter={false}
                />
              </Box>
            </Box>
          </Box>

          <Box
            style={{
              borderBottom: '1px solid #E2E8F0',
              minWidth: 0,
              marginBottom: -8,
            }}
          >
            <Tabs
              activeTab={activeTab}
              handleTabChange={(_, nextTab) => {
                setActiveTab(nextTab as EndpointDetailsTab);
              }}
              tabs={tabs}
            />
          </Box>
        </Box>

        {activeTab === 'dashboard' && (
          <EndpointDashboardTab
            systemWazuhAgentId={systemWazuhAgentId}
            displayName={displayName}
          />
        )}
        {activeTab === 'events' && <EndpointEventsTab />}
        {activeTab === 'vulnerabilities' && (
          <EndpointVulnerabilitiesTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeTab === 'activity-timeline' && <EndpointActivityTimelineTab />}
        {activeTab === 'compliance' && (
          <EndpointComplianceTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
        {activeTab === 'technical-details' && (
          <EndpointTechnicalDetailsTab systemWazuhAgentId={systemWazuhAgentId} />
        )}
      </Box>
    </>
  );
};

export default EndpointDetails;
